/* eslint-disable react-refresh/only-export-components -- Registrierungsdatei (Andockstelle), keine Komponenten */
// Andockstelle für das Frontend-Paket „Extras" (Admin-Panel, Suche, Lesezeichen, geplante Nachrichten,
// Erinnerungen, Export, Bots/Webhooks, Soundboard).
// Die App-Shell (Paket „Kern") bindet diese Registrierungen ein und rendert sie; das Paket „Extras"
// füllt die Listen. Beide Seiten ändern nur ihre eigenen Dateien.
import type { ChannelType, CurrentUserDto } from '@lagerfeuer/shared';
import { useAuthStore } from '../../store/auth';
import type { ComponentType } from 'react';
import { adminMenuItems, adminRoutes } from './admin';
import { bookmarksMenuItems, bookmarksRoutes } from './bookmarks';
import { botsMenuItems } from './bots';
import { exportsMenuItems, exportsRoutes } from './exports';
import { ReminderWatcher, remindersMenuItems, remindersRoutes } from './reminders';
import { scheduledMenuItems, scheduledRoutes } from './scheduled';
import { searchMenuItems, searchRoutes } from './search';
import { soundboardMenuItems } from './soundboard-menu';
import './toast-standalone';
import { ExtrasToaster } from './ui';
import { webhooksMenuItems } from './webhooks';

/** Eigene Seite unter einem Pfad der App (z. B. `/admin/*`, `/bookmarks`). */
export interface ExtraRoute {
  path: string;
  element: ComponentType;
}

/** Eintrag in einem Menü der Shell. `slot` bestimmt, wo die Shell ihn zeigt. */
export interface ExtraMenuItem {
  id: string;
  label: string;
  slot: 'userMenu' | 'serverMenu' | 'channelHeader' | 'messageMenu' | 'userSettings' | 'serverSettings';
  /** Für Seiten-Einträge: Ziel-Pfad; Platzhalter `:serverId`, `:channelId`, `:messageId` ersetzt die Shell. */
  to?: string;
  /** Für eingebettete Einstellungs-Tabs und Dialoge. Props: Kontext-IDs der Shell. */
  component?: ComponentType<ExtraContext>;
  /**
   * Nur für Plattform-Admins. `extraMenuItems` enthält solche Einträge nur, solange der angemeldete Nutzer Admin
   * ist (wird bei jeder Sitzungsänderung nachgeführt); die Seiten prüfen selbst nochmals, der Server sowieso.
   */
  adminOnly?: boolean;
  /**
   * Darstellung von `component`: 'dialog' = bringt eigenes Dialogfenster mit (Shell rendert sie direkt, ohne
   * eigenes Modal); 'panel' = Inhalt für ein Modal/Popover der Shell; 'tab' = Einstellungs-Tab. Standard 'panel'.
   */
  display?: 'dialog' | 'panel' | 'tab';
  /** Nur für serverSettings: nötiges Serverrecht (Bit); fehlt = wie die übrigen Tabs (irgendein Verwaltungsrecht). */
  serverPermission?: bigint;
  /** Nur in diesen Kanaltypen anzeigen (channelHeader); fehlt = überall. */
  channelTypes?: readonly ChannelType[];
}

export interface ExtraContext {
  serverId?: string;
  channelId?: string;
  messageId?: string;
  onClose?: () => void;
}

export const extraRoutes: ExtraRoute[] = [
  ...adminRoutes,
  ...searchRoutes,
  ...scheduledRoutes,
  ...remindersRoutes,
  ...bookmarksRoutes,
  ...exportsRoutes,
];

const allMenuItems: ExtraMenuItem[] = [
  ...searchMenuItems,
  ...scheduledMenuItems,
  ...remindersMenuItems,
  ...bookmarksMenuItems,
  ...exportsMenuItems,
  ...soundboardMenuItems,
  ...botsMenuItems,
  ...webhooksMenuItems,
  ...adminMenuItems,
];

/**
 * Menüeinträge für die Shell. Bleibt dasselbe Array-Objekt, Admin-Einträge werden aber bei jeder Sitzungsänderung
 * ein- bzw. ausgehängt – so sieht ein Nicht-Admin den Admin-Eintrag auch dann nicht, wenn die Shell `adminOnly`
 * nicht selbst filtert.
 */
export const extraMenuItems: ExtraMenuItem[] = [];

function syncMenuItems(isAdmin: boolean): void {
  extraMenuItems.splice(0, extraMenuItems.length, ...allMenuItems.filter((it) => !it.adminOnly || isAdmin));
}
syncMenuItems(!!useAuthStore.getState().user?.isAdmin);
useAuthStore.subscribe((s, prev) => {
  if (!!s.user?.isAdmin !== !!prev.user?.isAdmin) syncMenuItems(!!s.user?.isAdmin);
});

/**
 * Global (einmal, innerhalb von Router + QueryClientProvider) zu rendernde Komponenten:
 * Toasts und der Erinnerungs-Wächter (Toast bei Fälligkeit).
 */
export const extraGlobals: ComponentType[] = [ExtrasToaster, ReminderWatcher];

/** Menüeinträge eines Slots, gefiltert nach Admin-Recht des Nutzers. */
export function menuItemsFor(slot: ExtraMenuItem['slot'], user: Pick<CurrentUserDto, 'isAdmin'> | null | undefined): ExtraMenuItem[] {
  return allMenuItems.filter((it) => it.slot === slot && (!it.adminOnly || !!user?.isAdmin));
}

export { bindExtrasRealtime } from './realtime';
export { Soundboard } from './soundboard';
export { setSoundboardVoiceState, stopSoundboardSounds, type SoundboardVoiceState } from './soundboard-utils';
